/**
 * Pure data layer for marker state management.
 * No DOM or map API dependency — just data + diff logic.
 */

import type { TakContact, NormalizedSaEvent, Track, CotAffiliation, DisplayFieldDef } from '@vtak/shared';
import { cotTypeToColor } from '@vtak/shared';
import { CLIENT_CONFIG } from '@/config';
import { useTrackClassificationStore } from '@/stores/track-classification-store';
import { useTrackFilterStore } from '@/stores/track-filter-store';
import { findLayerDefinition } from '@/components/map/plugin-layer-manager';

/** CoT affiliation characters by affiliation, for classification overrides. */
const AFFILIATION_CHARS: ReadonlyMap<CotAffiliation, string> = new Map([
  ['friendly', 'f'],
  ['hostile', 'h'],
  ['neutral', 'n'],
  ['unknown', 'u'],
]);

/** Replace the affiliation char (2nd segment) of a CoT type code. */
export function withAffiliation(type: string, affiliation: CotAffiliation): string {
  const parts = type.split('-');
  if (parts.length < 2) return type;
  const char = AFFILIATION_CHARS.get(affiliation);
  if (char !== undefined) parts[1] = char;
  return parts.join('-');
}

/** A labeled key/value pair surfaced in the marker popup. */
export interface MarkerDetail {
  label: string;
  value: string;
}

/** Unified marker data for rendering. `layer` is the layer key (e.g. 'contact', 'cot', 'air:aircraft'). */
export interface MarkerData {
  uid: string;
  position: google.maps.LatLngLiteral;
  cotType: string;
  callsign: string;
  team: string;
  role: string;
  lastSeen: string;
  color: string;
  layer: string;
  /** Absolute altitude in meters for 3D rendering; undefined clamps to ground. */
  altitude?: number;
  /** Declared display-field rows for the popup (from the layer manifest). */
  details?: MarkerDetail[];
  /** Pictorial glyph key for 3D pins (from the layer manifest). */
  icon?: string;
  /** Image URL (e.g. live camera snapshot) to show in the popup. */
  imageUrl?: string;
  /** Exercise/simulated track — rendered with a distinct SIM style. */
  simulated?: boolean;
}

/** Diff result between two marker sets */
export interface MarkerDiff {
  added: MarkerData[];
  removed: string[];
  updated: MarkerData[];
  /** Markers whose symbol/affiliation changed and must be re-created. */
  reclassified: MarkerData[];
}

/** Registry of active markers keyed by UID */
const markerRegistry = new Map<string, MarkerData>();

/** Get all current markers */
export function getAllMarkers(): ReadonlyMap<string, MarkerData> {
  return markerRegistry;
}

/** Get a single marker by UID */
export function getMarker(uid: string): MarkerData | undefined {
  return markerRegistry.get(uid);
}

/** Convert TAK contacts to MarkerData array */
export function contactsToMarkerData(contacts: TakContact[]): MarkerData[] {
  return contacts
    .filter((c) => c.lat !== undefined && c.lon !== undefined)
    .map((c) => ({
      uid: c.uid ?? '',
      position: { lat: c.lat!, lng: c.lon! },
      cotType: 'a-f-G-U-C',
      callsign: c.callsign ?? 'Unknown',
      team: c.team ?? '',
      role: c.role ?? '',
      lastSeen: c.lastEventTime ?? '',
      color: '#64b5f6',
      layer: 'contact',
    }));
}

/** Convert normalized SA events to MarkerData array */
export function eventsToMarkerData(events: NormalizedSaEvent[]): MarkerData[] {
  return events.map((evt) => ({
    uid: evt.uid,
    position: { lat: evt.lat, lng: evt.lon },
    cotType: evt.type,
    callsign: evt.callsign,
    team: '',
    role: '',
    lastSeen: '',
    color: cotTypeToColor(evt.type),
    layer: 'cot',
  }));
}

/**
 * Convert external feed tracks to MarkerData: applies the type-filter, operator
 * classification overrides, orbital-shell altitude, and declared popup fields.
 */
export function tracksToMarkerData(tracks: Track[], layerId: string): MarkerData[] {
  const overrides = useTrackClassificationStore.getState().byUid;
  const definition = findLayerDefinition(layerId);
  const fields = definition?.displayFields ?? [];
  const icon = definition?.icon;
  const imageField = definition?.imageField;
  return applyCategoryFilter(tracks, layerId).map((track) =>
    trackToMarkerData(track, layerId, overrides, fields, icon, imageField),
  );
}

/** Build a single track's MarkerData (classification override + altitude + details + icon + image). */
function trackToMarkerData(
  track: Track,
  layerId: string,
  overrides: ReadonlyMap<string, { affiliation: CotAffiliation }>,
  fields: DisplayFieldDef[],
  icon: string | undefined,
  imageField: string | undefined,
): MarkerData {
  const override = overrides.get(track.uid);
  const cotType = override ? withAffiliation(track.type, override.affiliation) : track.type;
  // A track may declare its own glyph (e.g. per-kind base locations); the
  // layer-level icon is the fallback.
  const trackIcon = typeof track.props['icon'] === 'string' ? track.props['icon'] : undefined;
  return {
    uid: track.uid,
    position: { lat: track.lat, lng: track.lon },
    cotType,
    callsign: track.callsign,
    team: '',
    role: '',
    lastSeen: '',
    color: cotTypeToColor(cotType),
    layer: layerId,
    altitude: shellAltitude(track),
    details: buildDetails(track, fields),
    icon: trackIcon ?? icon,
    imageUrl: imageUrlFor(track, imageField),
    simulated: track.props['simulated'] === true,
  };
}

/** Read a track's image URL from the declared image field, if any. */
function imageUrlFor(track: Track, imageField: string | undefined): string | undefined {
  if (!imageField) return undefined;
  const value = track.props[imageField];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Keep only tracks whose category is selected for the layer (empty set = keep all). */
function applyCategoryFilter(tracks: Track[], layerId: string): Track[] {
  const selected = useTrackFilterStore.getState().selectedFor(layerId);
  if (!selected || selected.size === 0) return tracks;
  return tracks.filter((track) => isTrackSelected(track, selected));
}

/** A track passes the filter when its category is selected; uncategorized tracks always pass. */
function isTrackSelected(track: Track, selected: Set<string>): boolean {
  return track.category === undefined || selected.has(track.category);
}

/** Absolute orbital-shell altitude (meters) from props.altitudeKm × exaggeration, or undefined. */
function shellAltitude(track: Track): number | undefined {
  const km = track.props['altitudeKm'];
  if (typeof km !== 'number') return undefined;
  return km * 1000 * CLIENT_CONFIG.satelliteShellExaggeration;
}

/** Map declared display fields to popup rows, skipping props the track does not carry. */
function buildDetails(track: Track, fields: DisplayFieldDef[]): MarkerDetail[] {
  return fields
    .filter((field) => track.props[field.key] !== undefined)
    .map((field) => ({ label: field.label, value: String(track.props[field.key]) }));
}

/** Compute diff between current registry and new data for a single layer */
export function computeDiff(incoming: MarkerData[], layer: string): MarkerDiff {
  const currentOfType = new Map<string, MarkerData>();
  for (const [uid, data] of markerRegistry) {
    if (data.layer === layer) {
      currentOfType.set(uid, data);
    }
  }

  const incomingMap = new Map(incoming.map((m) => [m.uid, m]));
  const added: MarkerData[] = [];
  const updated: MarkerData[] = [];
  const reclassified: MarkerData[] = [];
  const removed: string[] = [];

  for (const [uid, data] of incomingMap) {
    const prev = currentOfType.get(uid);
    if (!prev) {
      added.push(data);
    } else if (prev.cotType !== data.cotType) {
      reclassified.push(data);
    } else if (hasMoved(prev, data)) {
      updated.push(data);
    }
  }

  for (const uid of currentOfType.keys()) {
    if (!incomingMap.has(uid)) {
      removed.push(uid);
    }
  }

  return { added, removed, updated, reclassified };
}

/** Apply a diff to the registry */
export function applyDiff(diff: MarkerDiff, incoming: MarkerData[]): void {
  for (const uid of diff.removed) {
    markerRegistry.delete(uid);
  }
  for (const data of incoming) {
    markerRegistry.set(data.uid, data);
  }
}

/** Clear all markers from registry */
export function clearRegistry(): void {
  markerRegistry.clear();
}

/** Check if position or altitude changed between two markers */
function hasMoved(prev: MarkerData, next: MarkerData): boolean {
  return (
    prev.position.lat !== next.position.lat ||
    prev.position.lng !== next.position.lng ||
    prev.altitude !== next.altitude
  );
}
